import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, TrendingDown, Shield, AlertTriangle, 
  Target, PieChart, BarChart3, ArrowUpRight, ArrowDownRight,
  Calculator, Percent, Clock, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend
} from 'recharts';

export default function FinancialImpactDashboard() {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/dashboard/financial-impact');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load financial impact data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`p-8 text-center ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
        <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Unable to load financial impact data</p>
      </div>
    );
  }

  const { summary, exposure_by_severity, monthly_trend, roi_analysis } = data;

  // Prepare pie chart data
  const pieData = [
    { name: 'Critical', value: exposure_by_severity.critical.exposure, color: '#ef4444' },
    { name: 'High', value: exposure_by_severity.high.exposure, color: '#f97316' },
    { name: 'Medium', value: exposure_by_severity.medium.exposure, color: '#eab308' },
    { name: 'Low', value: exposure_by_severity.low.exposure, color: '#22c55e' }
  ].filter(d => d.value > 0);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  return (
    <div className={`space-y-6 p-6 ${isDarkMode ? 'bg-navy-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold font-rajdhani ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Financial Impact Dashboard
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Risk quantification using FAIR methodology
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className={isDarkMode ? 'border-white/10' : ''}>
          <BarChart3 className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Exposure */}
        <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-gradient-to-br from-red-500/20 to-orange-500/10 border border-red-500/30' : 'bg-gradient-to-br from-red-50 to-orange-50 border border-red-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <ArrowUpRight className="w-5 h-5 text-red-500" />
          </div>
          <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {summary.total_exposure_formatted}
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>Total Exposure</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
            Annualized Loss Expectancy
          </p>
        </div>

        {/* Resolved Savings */}
        <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/10 border border-emerald-500/30' : 'bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </div>
            <ArrowDownRight className="w-5 h-5 text-emerald-500" />
          </div>
          <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {summary.resolved_savings_formatted}
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Risk Mitigated</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
            From resolved vulnerabilities
          </p>
        </div>

        {/* Risk Reduction */}
        <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-gradient-to-br from-indigo-500/20 to-blue-500/10 border border-indigo-500/30' : 'bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
              <Percent className="w-5 h-5 text-indigo-500" />
            </div>
            <TrendingDown className="w-5 h-5 text-indigo-500" />
          </div>
          <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {summary.risk_reduction_percent}%
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>Risk Reduction</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
            Overall improvement
          </p>
        </div>

        {/* Potential Savings */}
        <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border border-amber-500/30' : 'bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
              <Target className="w-5 h-5 text-amber-500" />
            </div>
            <DollarSign className="w-5 h-5 text-amber-500" />
          </div>
          <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {summary.potential_annual_savings_formatted}
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>Potential Savings</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
            If critical/high fixed
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exposure by Severity */}
        <div className={`p-6 rounded-2xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <PieChart className="w-5 h-5 text-indigo-400" />
            Exposure by Severity
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1e1e2e' : '#fff',
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          
          {/* Severity Breakdown Table */}
          <div className="mt-4 space-y-2">
            {Object.entries(exposure_by_severity).map(([severity, data]) => (
              <div key={severity} className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    severity === 'critical' ? 'bg-red-500' :
                    severity === 'high' ? 'bg-orange-500' :
                    severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  <span className={`text-sm capitalize ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>{severity}</span>
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>({data.count} vulns)</span>
                </div>
                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{data.formatted}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className={`p-6 rounded-2xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Risk Trend (6 Months)
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'} />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: isDarkMode ? '#a1a1aa' : '#64748b', fontSize: 12 }}
                />
                <YAxis 
                  tickFormatter={formatCurrency}
                  tick={{ fill: isDarkMode ? '#a1a1aa' : '#64748b', fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1e1e2e' : '#fff',
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="exposure" 
                  stroke="#ef4444" 
                  fill="url(#colorExposure)" 
                  name="Exposure"
                />
                <Area 
                  type="monotone" 
                  dataKey="savings" 
                  stroke="#22c55e" 
                  fill="url(#colorSavings)" 
                  name="Savings"
                />
                <defs>
                  <linearGradient id="colorExposure" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ROI Analysis */}
      <div className={`p-6 rounded-2xl ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          <Calculator className="w-5 h-5 text-indigo-400" />
          ROI Analysis - Remediation Investment
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Est. Remediation Cost</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              ${roi_analysis.estimated_remediation_cost?.toLocaleString() || 0}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>For critical/high vulns</p>
          </div>
          
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Potential Loss Avoided</p>
            <p className={`text-2xl font-bold text-emerald-500`}>
              ${roi_analysis.potential_loss_avoided?.toLocaleString() || 0}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Annual savings</p>
          </div>
          
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>ROI</p>
            <p className={`text-2xl font-bold ${roi_analysis.roi_percent > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {roi_analysis.roi_percent?.toFixed(0) || 0}%
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Return on investment</p>
          </div>
          
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Payback Period</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {roi_analysis.payback_period_months ? `${roi_analysis.payback_period_months} mo` : 'N/A'}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Break-even time</p>
          </div>
        </div>

        {/* ROI Visual */}
        {roi_analysis.roi_percent > 0 && (
          <div className={`mt-4 p-4 rounded-xl ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-emerald-500" />
              <div>
                <p className={`font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  Strong Investment Case
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                  Remediating critical and high severity vulnerabilities would generate a {roi_analysis.roi_percent?.toFixed(0)}% return 
                  with payback in {roi_analysis.payback_period_months} month{roi_analysis.payback_period_months !== 1 ? 's' : ''}.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Methodology Note */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'}`}>
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-indigo-500 mt-0.5" />
          <div>
            <p className={`font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>FAIR Methodology</p>
            <p className={`text-sm ${isDarkMode ? 'text-indigo-200/70' : 'text-indigo-600'}`}>
              This dashboard uses the Factor Analysis of Information Risk (FAIR) model for risk quantification. 
              Estimates are based on industry-average breach costs and annualized breach probabilities by severity level.
              Actual costs may vary based on your organization's specific risk profile and asset values.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
